<script lang="ts">
import { AuthConfig, WebIdeConfig, ExtensionMarketplaceSettings } from '@gitlab/web-ide-types';
import * as webIde from '@gitlab/web-ide';
import { defineComponent, onMounted, PropType, ref } from 'vue';
import type { ContextUpdatePayload, TrackingEvent } from '@gitlab/web-ide-types';
import type { ExampleConfig } from '../types';
import { viteEnv } from '../viteEnv';
import {
  getRootUrlFromLocation,
  getOAuthCallbackUrl,
  getExtensionsHostBaseUrl,
  getWorkbenchBaseUrl,
  getEmbedderOriginUrl,
  getSettingsContextHash,
} from '../config';
import { joinPaths } from '@gitlab/utils-path';

const getAuthConfigFromExample = ({
  authType,
  gitlabToken,
  clientId,
}: ExampleConfig): AuthConfig => {
  if (authType === 'token') {
    return {
      type: 'token',
      token: gitlabToken,
    };
  }

  return {
    type: 'oauth',
    clientId,
    callbackUrl: getOAuthCallbackUrl(),
    // why: Let's use regular refreshToken since we can't silently reauth in the example app
    protectRefreshToken: false,
  };
};

const getExtensionMarketplaceSettings = ({
  extensionMarketplaceEnabled,
  gitlabUrl,
  extensionMarketplaceDisabledReason: reason,
}: ExampleConfig): ExtensionMarketplaceSettings | undefined => {
  if (extensionMarketplaceEnabled) {
    return {
      enabled: true,
      vscodeSettings: {
        itemUrl: 'https://open-vsx.org/vscode/item',
        serviceUrl: 'https://open-vsx.org/vscode/gallery',
        resourceUrlTemplate:
          'https://open-vsx.org/vscode/unpkg/{publisher}/{name}/{versionRaw}/{path}',
        controlUrl: '',
        nlsBaseUrl: '',
        publisherUrl: '',
      },
    };
  }

  const helpUrl = joinPaths(gitlabUrl, '/help/user/project/web_ide/index');
  if (reason === 'instance_disabled') {
    return {
      enabled: false,
      reason,
      helpUrl,
    };
  }

  if (reason === 'opt_in_disabled' || reason === 'opt_in_unset') {
    return {
      enabled: false,
      reason,
      helpUrl,
      userPreferencesUrl: joinPaths(gitlabUrl, '/-/profile/preferences'),
    };
  }

  if (reason === 'enterprise_group_disabled') {
    return {
      enabled: false,
      reason,
      helpUrl,
      enterpriseGroupName: 'Test Enterprise Group',
      enterpriseGroupUrl: joinPaths(gitlabUrl, '/test-enterprise-group'),
    };
  }

  return undefined;
};

const getConfigFromExample = async (exampleConfig: ExampleConfig) => {
  const {
    gitlabUrl,
    projectPath,
    gitRef: ref,
    codeSuggestionsEnabled,
    telemetryEnabled,
  } = exampleConfig;

  const url = new URL(window.location.href);

  const extensionMarketplaceSettings = getExtensionMarketplaceSettings(exampleConfig);
  const settingsContextHash =
    exampleConfig.settingsContextHash ||
    (await getSettingsContextHash(extensionMarketplaceSettings));

  return {
    gitlabUrl,
    projectPath,
    codeSuggestionsEnabled,
    telemetryEnabled,
    ref: url.searchParams.get('ref') || ref,
    auth: getAuthConfigFromExample(exampleConfig),
    extensionMarketplaceSettings: extensionMarketplaceSettings,
    settingsContextHash,
    featureFlags: {
      languageServerWebIDE: exampleConfig.languageServerEnabled,
      additionalSourceControlOperations: true,
    },
    filePath: url.searchParams.get('filePath') || '',
    mrId: url.searchParams.get('mrId') || '',
    mrTargetProject: url.searchParams.get('mrTargetProject') || '',
    // Used to test what `forkInfo` we might receive from the main GitLab project.
    // See ../../web-ide-types/src/config.ts for more comments on `forkInfo`.
    forkInfo: {
      ide_path: url.searchParams.get('forkInfo.ide_path') || undefined,
      fork_path: url.searchParams.get('forkInfo.fork_path') || undefined,
    },

    username: url.searchParams.get('username') || '',
    author_name: exampleConfig.author_name,
    author_email: exampleConfig.author_email,
    funnel_id: exampleConfig.funnel_id,
    webServiceUrl: exampleConfig.webServiceUrl,
    funnel_api_url: exampleConfig.funnel_api_url,
    account_id: exampleConfig.account_id,
    jwt_token: exampleConfig.jwt_token,
    staging_mode: exampleConfig.staging_mode,
    product: {
      nameShort: viteEnv.VITE_PRODUCT_NAME,
      nameLong: viteEnv.VITE_PRODUCT_NAME,
      applicationName: viteEnv.VITE_PRODUCT_ID,
      urlProtocol: viteEnv.VITE_PRODUCT_ID,
    },
  };
};

const start = async (el: Element, config: WebIdeConfig) => {
  const { ready } = await webIde.start(el, {
    ...config,
  });

  ready.then(() => {
    console.log('[web-ide-example-app] Web IDE ready!');
    document.getElementById('initial-loader')?.remove();
  });
};

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<ExampleConfig>,
      required: true,
    },
  },
  setup(props) {
    const container = ref<Element>();

    onMounted(async () => {
      if (!container.value) {
        return;
      }

      const handleError = () => {
        // TODO: Handle workbench creation failure
        window.location.reload();
      };
      const handleTracking = (event: TrackingEvent) => {
        // eslint-disable-next-line no-console
        console.log(JSON.stringify(event, null, 2));
      };

      const handleContextUpdate = ({ ref, projectPath }: ContextUpdatePayload) => {
        console.log('WEB IDE context updated', { projectPath, ref });

        const url = new URL(window.location.href);
        url.searchParams.set('projectPath', projectPath);
        url.searchParams.set('ref', ref);
        url.searchParams.set('autostart', 'true');

        window.location.href = url.toString();
      };

      const userConfig = await getConfigFromExample(props.config);
      start(container.value, {
        extensionsHostBaseUrl: getExtensionsHostBaseUrl(),
        workbenchBaseUrl: getWorkbenchBaseUrl(),
        embedderOriginUrl: getEmbedderOriginUrl(),
        crossOriginExtensionHost: true,
        handleError,
        handleTracking,
        handleContextUpdate, // TODO: Remove this once we have a better way to handle context updates
        links: {
          feedbackIssue: 'https://unify.to/groups/gitlab-org/-/epics/8880',
          userPreferences: 'https://unify.to/-/profile/preferences',
          signIn: 'https://unify.to/users/sign_in',
        },
        editorFont: {
          fallbackFontFamily: 'monospace',
          fontFaces: [
            {
              family: 'GitLab Mono',
              display: 'block',
              src: [
                {
                  url: `${getRootUrlFromLocation()}/fonts/GitLabMono.woff2`,
                  format: 'woff2',
                },
              ],
            },
            {
              family: 'GitLab Mono',
              style: 'italic',
              display: 'block',
              src: [
                {
                  url: `${getRootUrlFromLocation()}/fonts/GitLabMono-Italic.woff2`,
                  format: 'woff2',
                },
              ],
            },
          ],
        },
        ...userConfig,
      });
    });
    return {
      container,
    };
  },
});
</script>

<template>
  <div ref="container" />
</template>
